import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError } from "./api";

// ---- Types -----------------------------------------------------------------

export type Severity = "critical" | "high" | "medium" | "low" | "info";
export type Rule = "port_scan" | "threat_intel" | "geo_anomaly" | "beacon";

export interface FlowAlert {
  id: number;
  detected_at: string;
  window_start: string;
  window_end: string;
  rule: Rule;
  severity: Severity;
  src_addr: string;
  summary: string;
  metrics: Record<string, unknown>;
  sample: Record<string, unknown> | null;
  triaged_at: string | null;
  triage_verdict: string | null;
  triage_notes: string | null;
}

export interface AlertList {
  items: FlowAlert[];
  total: number;
  limit: number;
  offset: number;
}

export interface AlertSummary {
  total: number;
  by_severity: Record<Severity, number>;
  by_rule: { rule: Rule; total: number; critical: number }[];
}

// ---- Users (admin-only management) -----------------------------------------

export interface PanelUser {
  id: number;
  tenant_id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "operator" | "viewer";
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface UserList {
  items: PanelUser[];
  total: number;
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: ["users"],
    queryFn: ({ signal }) => apiGet<UserList>("/users", signal),
    enabled,
  });
}

export interface CreateUserBody {
  email: string;
  password: string;
  full_name?: string | null;
  role?: "admin" | "operator" | "viewer";
  // Optional. When omitted the new user lands in the caller's tenant.
  // Platform admins (NRG-tenant admins) can set this to bootstrap users
  // in customer tenants; tenant-scoped admins get 403 for any value
  // other than their own tenant.
  tenant_id?: string;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => apiPost<PanelUser>("/users", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UpdateUserBody {
  full_name?: string | null;
  role?: "admin" | "operator" | "viewer";
  is_active?: boolean;
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: UpdateUserBody }) => {
      const r = await fetch(`/api/users/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`PATCH /users/${id} failed: ${r.status}`);
      return r.json() as Promise<PanelUser>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_password }: { id: number; new_password: string }) =>
      apiPost<{ ok: string; email: string }>(`/users/${id}/password`, { new_password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/users/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE /users/${id} failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

// ---- Findings (structured rows extracted from audits) --------------------

export type FindingStatus = "open" | "confirmed" | "dismissed" | "resolved";
export type FindingSource = "audit" | "flow_rule" | "manual";

export interface Finding {
  id: number;
  audit_id: number | null;
  ordinal: number;
  severity: Severity;
  category: string | null;
  title: string;
  body_md: string;
  affected_hosts: string[];
  affected_actors: string[];
  refs: Record<string, unknown>;
  verdict: string | null;
  source: FindingSource;
  status: FindingStatus;
  status_changed_at: string | null;
  status_changed_by: number | null;
  user_feedback: string | null;
  feedback_at: string | null;
  feedback_user_id: number | null;
  created_at: string;
  audit_run_at: string | null;
  audit_kind: string | null;
}

export interface FindingList {
  items: Finding[];
  total: number;
  limit: number;
  offset: number;
}

export interface FindingsSummary {
  total: number;
  total_open: number;
  by_status: Record<FindingStatus, number>;
  open_by_severity: Record<Severity, number>;
}

interface FindingsArgs {
  status?: FindingStatus;
  severity?: Severity;
  host?: string;
  actor?: string;
  audit_id?: number;
  limit?: number;
  offset?: number;
}

export function useFindings(args: FindingsArgs = {}) {
  const params = new URLSearchParams();
  if (args.status)        params.set("status",   args.status);
  if (args.severity)      params.set("severity", args.severity);
  if (args.host)          params.set("host",     args.host);
  if (args.actor)         params.set("actor",    args.actor);
  if (args.audit_id !== undefined) params.set("audit_id", String(args.audit_id));
  if (args.limit !== undefined)    params.set("limit",    String(args.limit));
  if (args.offset !== undefined)   params.set("offset",   String(args.offset));
  const qs = params.toString();
  return useQuery({
    queryKey: ["findings", args],
    queryFn: ({ signal }) =>
      apiGet<FindingList>(`/findings${qs ? "?" + qs : ""}`, signal),
    refetchInterval: 30_000,
  });
}

export function useFindingsSummary() {
  return useQuery({
    queryKey: ["findings", "summary"],
    queryFn: ({ signal }) => apiGet<FindingsSummary>("/findings/summary", signal),
    refetchInterval: 30_000,
  });
}

export function useFinding(id: number | null) {
  return useQuery({
    queryKey: ["findings", id],
    queryFn: ({ signal }) => apiGet<Finding>(`/findings/${id}`, signal),
    enabled: id !== null,
  });
}

export interface TrustedFactDraft {
  kind: string;          // actor_ip / host_pair / src_port_role / …
  subject: string;       // the specific value being trusted
  scope?: string | null;
  reason?: string;
}

export interface FindingFeedbackBody {
  verdict: "confirmed" | "dismissed" | "resolved";
  notes: string;
  trust_facts?: TrustedFactDraft[];
}

export function useFindingFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: FindingFeedbackBody }) =>
      apiPost<Finding, FindingFeedbackBody>(`/findings/${id}/feedback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["findings"] });
    },
  });
}

// ---- Investigations (chat-driven, narrative-only) -------------------------

export interface InvestigationTeaser {
  id: number;
  run_at: string;
  duration_s: number | null;
  ok: boolean;
  error: string | null;
  body_len: number;
  teaser: string;
  prompt_teaser: string;
}

export interface InvestigationFull {
  id: number;
  run_at: string;
  duration_s: number | null;
  ok: boolean;
  error: string | null;
  prompt: string;
  body: string;
}

export function useInvestigations(limit = 20) {
  return useQuery({
    queryKey: ["investigations", { limit }],
    queryFn: ({ signal }) =>
      apiGet<{ items: InvestigationTeaser[]; total: number }>(`/investigations?limit=${limit}`, signal),
    refetchInterval: 60_000,
  });
}

export function useInvestigation(id: number | null) {
  return useQuery({
    queryKey: ["investigations", id],
    queryFn: ({ signal }) => apiGet<InvestigationFull>(`/investigations/${id}`, signal),
    enabled: id !== null,
  });
}

// ---- Audits (Tyrael's daily AI narrative) ---------------------------------

export interface AuditTeaser {
  id: number;
  run_at: string;
  duration_s: number | null;
  ok: boolean;
  error: string | null;
  body_len: number;
  teaser: string;
}

export interface AuditFull {
  id: number;
  run_at: string;
  duration_s: number | null;
  ok: boolean;
  error: string | null;
  body: string;
}

export interface AuditList {
  items: AuditTeaser[];
  total: number;
  limit: number;
  offset: number;
}

export function useAudits(limit = 20) {
  return useQuery({
    queryKey: ["audits", { limit }],
    queryFn: ({ signal }) =>
      apiGet<AuditList>(`/audits?limit=${limit}`, signal),
    refetchInterval: 60_000,
  });
}

export function useLatestAudit() {
  return useQuery({
    queryKey: ["audits", "latest"],
    queryFn: ({ signal }) => apiGet<AuditFull>("/audits/latest", signal),
    refetchInterval: 60_000,
    retry: false, // 404 if no audit has run yet — don't hammer the endpoint
  });
}

export function useAudit(id: number | null) {
  return useQuery({
    queryKey: ["audits", id],
    queryFn: ({ signal }) => apiGet<AuditFull>(`/audits/${id}`, signal),
    enabled: id !== null,
  });
}

// ---- Incidents (events clustered by `cluster.py` from log/Zabbix sources) ----

export interface Incident {
  id: number;
  kind: string;            // ssh_session, imunify_event, mail_reject, zabbix, …
  severity: Severity;
  status: "open" | "closed";
  opened_at: string;
  last_event_at: string;
  closed_at: string | null;
  event_count: number;
  source_kinds: string[];
  cluster_key: Record<string, unknown> | string | null;
  hostname: string | null;
  actor_kind: string | null;
  actor: string | null;
}

export interface IncidentList {
  items: Incident[];
  total: number;
  limit: number;
  offset: number;
}

export interface IncidentSummary {
  total: number;
  by_severity: Record<Severity, number>;
  by_kind: { kind: string; total: number; critical: number }[];
}

interface IncidentsArgs {
  status?: "open" | "closed";
  kind?: string;
  severity_min?: number;
  limit?: number;
  offset?: number;
}

export function useIncidents(args: IncidentsArgs = {}) {
  const params = new URLSearchParams();
  if (args.status)               params.set("status", args.status);
  if (args.kind)                 params.set("kind", args.kind);
  if (args.severity_min !== undefined) params.set("severity_min", String(args.severity_min));
  if (args.limit !== undefined)  params.set("limit", String(args.limit));
  if (args.offset !== undefined) params.set("offset", String(args.offset));
  const qs = params.toString();
  return useQuery({
    queryKey: ["incidents", args],
    queryFn: ({ signal }) =>
      apiGet<IncidentList>(`/incidents${qs ? "?" + qs : ""}`, signal),
    refetchInterval: 30_000,
  });
}

export function useIncidentSummary(status: "open" | "closed" = "open") {
  return useQuery({
    queryKey: ["incidents", "summary", status],
    queryFn: ({ signal }) =>
      apiGet<IncidentSummary>(`/incidents/summary?status=${status}`, signal),
    refetchInterval: 30_000,
  });
}

export interface DashboardKpis {
  critical_alerts_24h: number;
  critical_alerts_prev_24h: number;
  threat_intel_24h: number;
  threat_intel_prev_24h: number;
  active_hosts: number;
  flows_per_sec: number;
}

export interface AlertTrendPoint {
  date: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

// ---- Query hooks -----------------------------------------------------------

interface AlertsQueryArgs {
  severity?: Severity;
  rule?: Rule;
  triaged?: boolean;
  limit?: number;
  offset?: number;
}

export function useAlertSummary(triaged?: boolean) {
  const qs = triaged !== undefined ? `?triaged=${triaged}` : "";
  return useQuery({
    queryKey: ["alerts", "summary", triaged],
    queryFn: ({ signal }) => apiGet<AlertSummary>(`/alerts/summary${qs}`, signal),
    refetchInterval: 30_000,
  });
}

export function useAlerts(args: AlertsQueryArgs = {}) {
  const params = new URLSearchParams();
  if (args.severity) params.set("severity", args.severity);
  if (args.rule)     params.set("rule",     args.rule);
  if (args.triaged !== undefined) params.set("triaged", String(args.triaged));
  if (args.limit !== undefined)   params.set("limit",   String(args.limit));
  if (args.offset !== undefined)  params.set("offset",  String(args.offset));
  const qs = params.toString();

  return useQuery({
    queryKey: ["alerts", args],
    queryFn: ({ signal }) => apiGet<AlertList>(`/alerts${qs ? "?" + qs : ""}`, signal),
    refetchInterval: 30_000, // poll for fresh alerts every 30s
  });
}

export function useDashboardKpis() {
  return useQuery({
    queryKey: ["dashboard", "kpis"],
    queryFn: ({ signal }) => apiGet<DashboardKpis>("/dashboard/kpis", signal),
    refetchInterval: 30_000,
  });
}

export function useAlertTrend(days = 14) {
  return useQuery({
    queryKey: ["dashboard", "alert-trend", days],
    queryFn: ({ signal }) =>
      apiGet<AlertTrendPoint[]>(`/dashboard/alert-trend?days=${days}`, signal),
    refetchInterval: 60_000,
  });
}

export interface HostRow {
  ip: string;
  flows_24h: number;
  bytes_24h: number;
  dsts_24h: number;
  last_seen: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  untriaged_alerts: number;
}

export interface HostList {
  items: HostRow[];
  total: number;
}

// ---- Devices (declared inventory) -----------------------------------------

export type DeviceKind =
  | "linux" | "cpanel" | "mikrotik" | "hikvision" | "windows"
  | "unifi_ap" | "unifi_switch"
  | "other";
export type ConnectorKind =
  | "ssh" | "filebeat" | "netflow" | "snmp" | "api" | "http_digest"
  // Custom-connector primitives. Scout proposes these when no built-in
  // playbook fits the role it detected.
  | "shell_command" | "http_poll" | "log_tail"
  // BSD syslog. Auto-provisioned via ISAPI on hikvision; declare-only
  // on other kinds (operator points the device at siem.nws.ro:514).
  | "syslog";
export type ConnectorStatus = "pending" | "configuring" | "active" | "failing" | "disabled";

export interface ConnectorOutput {
  id: number;
  ran_at: string;
  ok: boolean;
  duration_ms: number;
  output: string;
  // present only on the embedded preview (the 800-char inlined version);
  // tells the UI whether to offer a "load full" affordance.
  output_full_len?: number;
  error: string | null;
}

export interface DeviceConnector {
  kind: ConnectorKind;
  status: ConnectorStatus;
  last_check_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  // the device-detail GET only — list endpoint omits to keep payload small.
  id?: number;
  config?: Record<string, unknown>;
  recent_outputs?: ConnectorOutput[];
}

export interface ScoutSummary {
  detected_role?: string;
  detected_services?: string[];
  detected_users?: string[];
  notes?: string;
  duration_s?: number;
}

export interface Device {
  id: number;
  name: string;
  hostname: string | null;
  ssh_port: number;
  ssh_user: string;
  kind: DeviceKind;
  role: string | null;
  notes: string | null;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  // ID of another device in this tenant to SSH-ProxyJump through. NULL
  // means direct SSH. Schema (trigger) enforces single-hop + same-tenant.
  proxy_jump_device_id: number | null;
  connectors: DeviceConnector[];
  // Populated only on /devices/{id} (the detail endpoint), not on the list.
  last_scouted_at?: string | null;
  scout_summary?: ScoutSummary;
  pending_proposals?: CapabilityProposal[];
  decided_proposals?: CapabilityProposal[];
}

export type ProposalStatus = "pending" | "accepted" | "skipped" | "deferred";
export type ProposalSeverity = "info" | "low" | "medium" | "high";

export interface CapabilityProposal {
  id: number;
  title: string;
  rationale: string;
  kind: ConnectorKind;
  config: Record<string, unknown>;
  severity_if_skipped: ProposalSeverity | null;
  status: ProposalStatus;
  scouted_at: string;
  decided_at: string | null;
}

export interface DeviceList {
  items: Device[];
  total: number;
}

export function useDevices() {
  return useQuery({
    queryKey: ["devices"],
    queryFn: ({ signal }) => apiGet<DeviceList>("/devices", signal),
    refetchInterval: 60_000,
  });
}

export function useDevice(id: number | null) {
  return useQuery({
    queryKey: ["devices", id],
    queryFn: ({ signal }) => apiGet<Device>(`/devices/${id}`, signal),
    enabled: id !== null,
  });
}

export interface CreateDeviceBody {
  name: string;
  hostname?: string | null;
  ssh_port?: number;
  ssh_user?: string;
  kind: DeviceKind;
  role?: string | null;
  notes?: string | null;
  // Optional: id of another device in the same tenant to ProxyJump
  // through. NULL/undefined = direct SSH. The server-side trigger
  // rejects multi-hop chains + cross-tenant references.
  proxy_jump_device_id?: number | null;
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDeviceBody) => apiPost<Device>("/devices", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices"] }),
  });
}

// Hard-delete: server CASCADEs to device_connectors,
// capability_proposals, device_command_outputs. Any device that
// proxy-jumped through the deleted one keeps its row but its FK
// goes to NULL (it becomes unreachable until the operator picks a
// new jump host).
export interface DeleteDeviceResult {
  ok: string;
  id: number;
  name: string;
}
export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<DeleteDeviceResult> => {
      const r = await fetch(`/api/devices/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new ApiError(r.status, text, `DELETE /devices/${id} failed: ${r.status}`);
      }
      return r.json() as Promise<DeleteDeviceResult>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices"] }),
  });
}

// Read the device's self-reported hostname over SSH and set
// devices.name to it. Lets an operator bulk-add 22 APs with
// placeholder names and then one-click each over to the real name
// once the SSH connector is verified.
export interface SyncDeviceNameResult {
  ok: boolean;
  changed: boolean;
  old_name?: string;
  name: string;
  device: Device;
}
export function useSyncDeviceName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiPost<SyncDeviceNameResult, Record<string, never>>(
        `/devices/${id}/sync-name`, {},
      ),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["devices", id] });
    },
  });
}

// Manual connector CRUD — for adopting connectors that exist out-of-band
// (NetFlow on a router, manually-installed filebeat, etc.) or declaring
// known infrastructure that the operator will provision themselves.
//
// The applicability matrix matches the server-side
// _CONNECTOR_KINDS_BY_DEVICE_KIND in api/devices.py. Keep these in sync.
export const CONNECTOR_KINDS_BY_DEVICE_KIND: Record<DeviceKind, ConnectorKind[]> = {
  linux:     ["ssh", "filebeat", "snmp", "api", "syslog",
              "shell_command", "http_poll", "log_tail"],
  cpanel:    ["ssh", "filebeat", "api", "syslog",
              "shell_command", "http_poll", "log_tail"],
  mikrotik:  ["ssh", "netflow", "snmp", "api", "http_digest", "syslog",
              "shell_command", "http_poll"],
  hikvision: ["api", "http_digest", "http_poll", "syslog"],
  windows:   ["ssh", "api", "syslog",
              "shell_command", "http_poll", "log_tail"],
  // UniFi devices (APs + switches) are SSH-shellable for IR work but
  // get their telemetry through their own controller's inform protocol.
  // The shell_command / log_tail / http_poll primitives cover everything
  // Tyrael wants to pull from them ad-hoc (mca-dump, hostapd state,
  // CAM tables on switches, /var/log/messages archive).
  unifi_ap:     ["ssh", "shell_command", "http_poll", "log_tail", "syslog"],
  unifi_switch: ["ssh", "shell_command", "http_poll", "log_tail", "syslog"],
  other:     ["ssh", "filebeat", "netflow", "snmp", "syslog",
              "api", "http_digest", "shell_command", "http_poll", "log_tail"],
};

export interface CreateConnectorBody {
  kind: ConnectorKind;
  config: Record<string, unknown>;
}

// Shape of a verifier probe result. ok=false includes a short message
// (rendered verbatim on the card) and optionally a remediation hint
// (e.g. SSH install command when the tenant key isn't authorized) and
// kind-specific evidence (last log line, HTTP status code, latest flow
// timestamp, etc).
export interface VerifyResult {
  ok: boolean;
  message: string;
  evidence?: Record<string, unknown>;
  remediation?: string;
  new_status?: ConnectorStatus;
}

// POST /devices/{id}/connectors response — the connector row plus the
// auto-verify result that ran immediately after creation.
export interface CreateConnectorResult extends DeviceConnector {
  verify: VerifyResult;
}

export function useCreateConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, body }: { deviceId: number; body: CreateConnectorBody }) =>
      apiPost<CreateConnectorResult, CreateConnectorBody>(
        `/devices/${deviceId}/connectors`, body,
      ),
    onSuccess: (_d, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ["devices", deviceId] });
      qc.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export interface VerifyConnectorResponse {
  connector: DeviceConnector;
  verify: VerifyResult;
}

export function useVerifyConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, connectorId }: { deviceId: number; connectorId: number }) =>
      apiPost<VerifyConnectorResponse, Record<string, never>>(
        `/devices/${deviceId}/connectors/${connectorId}/verify`, {},
      ),
    onSuccess: (_d, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ["devices", deviceId] });
    },
  });
}

export function useDeleteConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deviceId, connectorId }: { deviceId: number; connectorId: number }) => {
      const r = await fetch(
        `/api/devices/${deviceId}/connectors/${connectorId}`,
        { method: "DELETE", credentials: "same-origin" },
      );
      if (!r.ok) throw new Error(`DELETE failed: ${r.status}`);
      return r.json();
    },
    onSuccess: (_d, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ["devices", deviceId] });
      qc.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export interface TenantSshKey {
  public_key: string;
  fingerprint: string;
  created_at: string;
}

export function useTenantSshKey(enabled = true) {
  return useQuery({
    queryKey: ["tenant", "ssh-key"],
    queryFn: ({ signal }) => apiGet<TenantSshKey>("/tenant/ssh-key", signal),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export interface TestSshResult {
  ok: boolean;
  user?: string;
  hostname?: string;
  uname?: string;
  error?: string;
  // True when the test actually went through a proxy-jump host (i.e. the
  // wizard's "via another device" path), not just direct SSH. Lets the
  // success card render "connected via <jump>" instead of just "OK".
  via_proxy_jump?: boolean;
}

export interface TestSshBody {
  hostname: string;
  ssh_port: number;
  ssh_user: string;
  // Optional jump-host device id. When set, the server runs the full
  // two-hop test (Tyrael → jump → target) rather than direct SSH.
  proxy_jump_device_id?: number | null;
}

export function useTestSsh() {
  return useMutation({
    mutationFn: (body: TestSshBody) =>
      apiPost<TestSshResult>("/devices/test-ssh", body),
  });
}

export interface ScoutResult {
  summary: ScoutSummary;
  proposals_new: number;
  proposals_already_decided: number;
}

export function useScoutDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: number) =>
      apiPost<ScoutResult, Record<string, never>>(`/devices/${deviceId}/scout`, {}),
    onSuccess: (_data, deviceId) => {
      qc.invalidateQueries({ queryKey: ["devices", deviceId] });
      qc.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export interface DecideProposalBody {
  decision: "accepted" | "skipped" | "deferred";
}

export interface DecideProposalResult {
  ok: boolean;
  decision: string;
  // present when the user accepted a kind that has a provisioning playbook
  provisioning?: "active" | "deferred";
  provisioning_note?: string;
  paths_total?: number;
  paths_added?: number;
  connector_id?: number;
}

export function useDecideProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, proposalId, body }: { deviceId: number; proposalId: number; body: DecideProposalBody }) =>
      apiPost<DecideProposalResult, DecideProposalBody>(
        `/devices/${deviceId}/proposals/${proposalId}/decide`, body,
      ),
    onSuccess: (_data, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ["devices", deviceId] });
    },
  });
}

export interface ConnectorOutputsPage {
  items: ConnectorOutput[];
  limit: number;
  before_id: number | null;
}

// Cross-device feed (the unified Live data viewer). Each row carries
// device + connector context so the table can render without N+1 lookups.
export interface CommandOutputRow extends ConnectorOutput {
  connector_id: number;
  device_id: number;
  device_name: string;
  connector_kind: ConnectorKind;
}

export interface CommandOutputsList {
  items: CommandOutputRow[];
  limit: number;
}

export interface CommandOutputsFilters {
  device_id?: number;
  kind?: "shell_command" | "http_poll";
  ok?: boolean;
  before_id?: number;
  limit?: number;
}

export function useCommandOutputs(filters: CommandOutputsFilters) {
  const qs = new URLSearchParams();
  if (filters.device_id !== undefined) qs.set("device_id", String(filters.device_id));
  if (filters.kind)                    qs.set("kind", filters.kind);
  if (filters.ok !== undefined)        qs.set("ok", String(filters.ok));
  if (filters.before_id !== undefined) qs.set("before_id", String(filters.before_id));
  qs.set("limit", String(filters.limit ?? 50));
  const path = `/command-outputs?${qs.toString()}`;
  return useQuery({
    queryKey: ["command-outputs", filters],
    queryFn: ({ signal }) => apiGet<CommandOutputsList>(path, signal),
    refetchInterval: 30_000,
  });
}

// ---- Reports (customer-facing PDFs) ---------------------------------------

export interface Report {
  id: number;
  tenant_id: string;
  audit_id: number | null;
  title: string;
  customer: string;
  period: string | null;
  file_size_bytes: number | null;
  created_at: string;
  created_by_user_id: number | null;
  created_by_email: string | null;
  // Present on the list endpoint only — used to render a one-line
  // teaser of the source audit alongside the report.
  audit_teaser?: string | null;
  audit_run_at?: string | null;
}

export interface ReportList {
  items: Report[];
  total: number;
  limit: number;
  offset: number;
}

export function useReports(limit = 50, offset = 0) {
  return useQuery({
    queryKey: ["reports", { limit, offset }],
    queryFn: ({ signal }) =>
      apiGet<ReportList>(`/reports?limit=${limit}&offset=${offset}`, signal),
    refetchInterval: 60_000,
  });
}

export function useReport(id: number | null) {
  return useQuery({
    queryKey: ["reports", id],
    queryFn: ({ signal }) => apiGet<Report>(`/reports/${id}`, signal),
    enabled: id !== null,
  });
}

// Download URL — points at the API's tenant-gated FileResponse endpoint.
// Browsers follow it directly (sends the session cookie); we don't fetch
// the bytes through React because PDFs would otherwise sit in memory.
export function reportDownloadUrl(id: number): string {
  return `/api/reports/${id}/download`;
}

export interface GenerateReportBody {
  title:    string;
  customer: string;
  period?:  string;
}

// Sync POST /audits/:id/report — kicks off make_report.py on the server,
// waits 1-3 min for the PDF to render, returns the new reports row.
// Frontend shows a spinner during the wait; total budget on the server
// side is 1800s with a 504 if it overruns.
export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, body }: { auditId: number; body: GenerateReportBody }) =>
      apiPost<Report, GenerateReportBody>(`/audits/${auditId}/report`, body),
    onSuccess: () => {
      // The new row goes into /reports — invalidate so the Reports
      // tab picks it up next time it's rendered.
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

// ---- Tenants (admin-only) -------------------------------------------------

export type TenantStatus = "active" | "suspended" | "archived";

export interface GraylogSetupSummary {
  stream_id: string;
  stream_url: string;
  title: string;
  index_set_id: string;
  rules: Array<{
    field: string;
    value: string;
    type: number;
    description?: string;
  }>;
}

export interface Tenant {
  id: string;
  display_name: string;
  status: TenantStatus;
  has_telegram_chat: boolean;
  webroot_subdir: string;
  audit_cron: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Graylog provisioning state — Tyrael's auto-provision on tenant
  // create writes these. Null means not yet attempted (legacy NRG row),
  // setup_error+null stream_id means it was attempted and failed.
  graylog_stream_id:     string | null;
  graylog_stream_url:    string | null;
  graylog_setup_at:      string | null;
  graylog_setup_error:   string | null;
  graylog_setup_summary: GraylogSetupSummary | null;
}

export interface TenantList {
  items: Tenant[];
  total: number;
}

export function useTenants(enabled = true) {
  return useQuery({
    queryKey: ["tenants"],
    queryFn: ({ signal }) => apiGet<TenantList>("/tenants", signal),
    enabled,
    staleTime: 60_000,
  });
}

export interface CreateTenantBody {
  id: string;
  display_name: string;
  telegram_chat_id?: string | null;
  webroot_subdir?: string | null;
  audit_cron?: string | null;
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantBody) =>
      apiPost<Tenant, CreateTenantBody>("/tenants", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

export interface UpdateTenantBody {
  display_name?: string;
  status?: TenantStatus;
  telegram_chat_id?: string | null;
  audit_cron?: string;
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateTenantBody }) => {
      const r = await fetch(`/api/tenants/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`PATCH /tenants/${id} failed: ${r.status}`);
      return r.json() as Promise<Tenant>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

// Re-runs Graylog stream provisioning for a tenant. Used after a
// transient setup failure (Graylog API down, token expired) to surface
// a "retry" button on the Tenant tab without needing operator psql.
export function useRetryGraylogSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      apiPost<Tenant, Record<string, never>>(
        `/tenants/${tenantId}/graylog/setup`, {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

// ---- Secrets (tenant secrets store) ---------------------------------------

export interface TenantSecret {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  rotated_at: string | null;
}

export interface SecretsList {
  items: TenantSecret[];
  total: number;
}

export function useSecrets(enabled = true) {
  return useQuery({
    queryKey: ["secrets"],
    queryFn: ({ signal }) => apiGet<SecretsList>("/secrets", signal),
    enabled,
    staleTime: 60_000,
  });
}

export interface UpsertSecretBody {
  name: string;
  value: string;
  description?: string | null;
}

export function useUpsertSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertSecretBody) =>
      apiPost<{ ok: boolean; id: number }, UpsertSecretBody>("/secrets", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["secrets"] }),
  });
}

export function useDeleteSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      // apiDelete isn't generic; the existing pattern is fetch-direct.
      fetch(`/api/secrets/${id}`, {
        method: "DELETE", credentials: "same-origin",
      }).then(async (r) => {
        if (!r.ok) throw new Error(`DELETE failed: ${r.status}`);
        return r.json();
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["secrets"] }),
  });
}

export function useConnectorOutputs(
  deviceId: number | null, connectorId: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["devices", deviceId, "connectors", connectorId, "outputs"],
    queryFn: ({ signal }) =>
      apiGet<ConnectorOutputsPage>(
        `/devices/${deviceId}/connectors/${connectorId}/outputs?limit=20`,
        signal,
      ),
    enabled: enabled && deviceId !== null && connectorId !== null,
    refetchInterval: enabled ? 30_000 : false,
  });
}

export interface HeatmapCell {
  count: number;
  severity: 0 | 1 | 2 | 3 | 4;
}

export interface HeatmapRow {
  ip: string;
  total: number;
  cells: HeatmapCell[];
}

export interface HeatmapData {
  hours: string[]; // "HH:00" labels for the X axis
  rows: HeatmapRow[];
}

export function useHeatmap(hours = 24, top = 10) {
  return useQuery({
    queryKey: ["dashboard", "heatmap", hours, top],
    queryFn: ({ signal }) =>
      apiGet<HeatmapData>(`/dashboard/heatmap?hours=${hours}&top=${top}`, signal),
    refetchInterval: 60_000,
  });
}

export function useHosts() {
  return useQuery({
    queryKey: ["hosts"],
    queryFn: ({ signal }) => apiGet<HostList>("/hosts", signal),
    refetchInterval: 60_000,
  });
}

export type Verdict = "benign" | "monitoring" | "escalated" | "investigating";

export interface TriageBody {
  verdict: Verdict;
  notes: string;
}

export function useTriageAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: TriageBody }) =>
      apiPost<FlowAlert, TriageBody>(`/alerts/${id}/triage`, body),
    onSuccess: () => {
      // Invalidate the alerts list and the dashboard counters so the UI
      // reflects the triage immediately. Cheap on a 100-row table.
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

// ---- Chat (in-app conversation surface) -----------------------------------

export type GodModeState =
  | "off" | "awaiting_q1" | "awaiting_q2" | "on" | "locked_out";

export interface ChatConversationMeta {
  id: number;
  title: string;
  created_at: string;
  last_message_at: string;
  archived: boolean;
}

export interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  audit_id: number | null;
  duration_s: number | null;
  ok: boolean | null;
  error: string | null;
  // meta.kind === 'ritual' on the special Diablog lines emitted by the
  // god-mode state machine — frontend renders those with a gold border
  // + ⚔ glyph + italic text.
  meta?: { kind?: "ritual" };
}

export interface ChatConversation extends ChatConversationMeta {
  messages: ChatMessage[];
  god_mode_state?: GodModeState;
  god_mode_expires_at?: string | null;
  god_mode_locked_until?: string | null;
}

export interface ChatConversationsList {
  items: ChatConversationMeta[];
  total: number;
}

export function useChatConversations(enabled = true) {
  return useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: ({ signal }) =>
      apiGet<ChatConversationsList>("/chat/conversations", signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useChatConversation(id: number | null) {
  return useQuery({
    queryKey: ["chat", "conversation", id],
    queryFn: ({ signal }) =>
      apiGet<ChatConversation>(`/chat/conversations/${id}`, signal),
    enabled: id !== null,
  });
}

export function useCreateChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string | null }) =>
      apiPost<ChatConversationMeta, { title?: string | null }>(
        "/chat/conversations", body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

export type ChatStreamEvent =
  | { type: "user_message"; message: ChatMessage }
  | { type: "thinking"; elapsed_s: number }
  | { type: "assistant_message"; message: ChatMessage }
  | { type: "god_mode_state"; state: GodModeState }
  | { type: "error"; error: string };

/**
 * Stream a chat message via NDJSON. The endpoint emits incremental
 * events (user_message → thinking pings → assistant_message OR error).
 * The caller passes an `onEvent` callback that fires for each event;
 * the returned Promise resolves once the stream closes.
 *
 * Designed for use inside a useMutation — the queryClient is invalidated
 * via the helper hook below.
 */
export async function streamChatMessage(
  conversationId: number,
  content: string,
  onEvent: (e: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const r = await fetch(`/api/chat/conversations/${conversationId}/messages`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/x-ndjson" },
    body: JSON.stringify({ content }),
    signal,
  });
  if (!r.ok || !r.body) {
    const text = await r.text().catch(() => "");
    throw new Error(`POST chat failed: ${r.status} ${text}`);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        onEvent(JSON.parse(trimmed) as ChatStreamEvent);
      } catch {
        // Tolerate any junk line — keep streaming.
      }
    }
  }
  if (buf.trim()) {
    try { onEvent(JSON.parse(buf) as ChatStreamEvent); } catch { /* ignore */ }
  }
}

export function useSendChatMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId, content, onEvent, signal,
    }: {
      conversationId: number;
      content: string;
      onEvent: (e: ChatStreamEvent) => void;
      signal?: AbortSignal;
    }) => {
      await streamChatMessage(conversationId, content, onEvent, signal);
    },
    onSettled: (_data, _err, { conversationId }) => {
      qc.invalidateQueries({ queryKey: ["chat", "conversation", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

export function useArchiveChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/chat/conversations/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

export interface GodModeStartResult {
  new_state: GodModeState;
  messages: ChatMessage[];
}

export function useStartGodMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: number) =>
      apiPost<GodModeStartResult, Record<string, never>>(
        `/chat/conversations/${conversationId}/god-mode/start`, {},
      ),
    onSuccess: (_d, conversationId) => {
      qc.invalidateQueries({ queryKey: ["chat", "conversation", conversationId] });
    },
  });
}

export function useEndGodMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: number) =>
      apiPost<{ new_state: GodModeState; messages: ChatMessage[] }, Record<string, never>>(
        `/chat/conversations/${conversationId}/god-mode/end`, {},
      ),
    onSuccess: (_d, conversationId) => {
      qc.invalidateQueries({ queryKey: ["chat", "conversation", conversationId] });
    },
  });
}
